import { useEffect, useState, useRef } from "react";
import { ref, onValue, set, update, remove, get, serverTimestamp } from "firebase/database";
import type { Auth } from "firebase/auth";
import type { Database, DatabaseReference } from "firebase/database";
import { signInAnonymously } from "firebase/auth";
import { auth, database, legacyAuth, legacyDatabase } from "../config/firebase";
import { ConnectionSessionError } from "../context/AppContext";
import { setGASessionUuid } from "../services/google_analytics";

export const ConnectionStatus = {
  INITIALIZING: "initializing",
  INITIALIZING_UUID_SESSION: "initializing_uuid_session",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
} as const;

type ConnectionStatusType =
  (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

interface ConnectionState {
  code: string | null;
  status: ConnectionStatusType;
  result: {
    left: {
      footLengthInMM: number
      archHeightInMM: number
    }
    right: {
      footLengthInMM: number
      archHeightInMM: number
    }
  } | {};
  sessionError: ConnectionSessionError | null;
  connectionError: string | null;
  instruction: string | null;
  countdown: "3" | "2" | "1" | null;
  scanning_foot: "left" | "right" | null;
  processing: boolean;
  active?: boolean;
  lastSeen?: number;
}

const MOBILE_INACTIVE_TIMEOUT_MS = 5 * 60 * 1000;

interface Backend {
  name: string;
  auth: Auth;
  database: Database;
}

// During the Firebase account migration the web app talks to both accounts.
// Whichever account the scanning app connects to wins; the other is discarded.
const BACKENDS: Backend[] = [
  { name: "primary", auth, database },
  // TODO(firebase-migration): remove legacy backend
  ...(legacyAuth && legacyDatabase
    ? [{ name: "legacy", auth: legacyAuth, database: legacyDatabase }]
    : []),
];

interface BackendSession {
  name: string;
  sessionRef: DatabaseReference;
  activeMapRef: DatabaseReference | null;
  inactiveMapRef: DatabaseReference | null;
}

interface SessionData {
  uuid: string;
  sessions: BackendSession[];
}

interface UseFirebaseConnectionOptions {
  initialSessionUuid?: string;
}

export const useFirebaseConnection = (options?: UseFirebaseConnectionOptions) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    code: null,
    status: options?.initialSessionUuid ? ConnectionStatus.INITIALIZING_UUID_SESSION : ConnectionStatus.INITIALIZING,
    result: null,
    sessionError: null,
    connectionError: null,
    instruction: null,
    countdown: null,
    scanning_foot: null,
    processing: false,
  });
  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  // Refs so event handlers always see the latest session data
  const sessionDataRef = useRef<SessionData | null>(null);
  const unsubscribersRef = useRef<Record<string, () => void>>({});
  const winnerRef = useRef<string | null>(null);
  const inactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commitSessionData = (data: SessionData | null) => {
    sessionDataRef.current = data;
    setSessionData(data);
  };

  const generateUniqueCode = async (backends: Backend[]): Promise<string> => {
    const activeCodes: Record<string, string> = {};
    await Promise.all(
      backends.map(async (backend) => {
        try {
          const snapshot = await get(ref(backend.database, "connections/active"));
          Object.assign(activeCodes, snapshot.val());
        } catch (error) {
          console.error(`Reading active codes failed (${backend.name}):`, error);
        }
      })
    );
    let newCode;
    do {
      newCode = Math.floor(1000 + Math.random() * 9000).toString();
    } while (newCode in activeCodes);
    return newCode;
  };

  const generateUUID = (): string => {
    return (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(
      /[018]/g,
      (c: any) =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
    );
  };

  const getStatusFromData = (data: any): ConnectionStatusType => {
    return data.didConnect
      ? data.closed
        ? ConnectionStatus.DISCONNECTED
        : ConnectionStatus.CONNECTED
      : ConnectionStatus.INITIALIZING;
  };

  const parseSessionData = (data: any) => {
    const status = getStatusFromData(data);
    const resultJSON = data.result ? JSON.parse(data.result) : {};
    const errorJSON = data.error ? JSON.parse(data.error) : {};

    return {
      code: data.code,
      status,
      result: resultJSON,
      sessionError: errorJSON,
      connectionError: data.connectionError,
      instruction: data.instruction,
      countdown: data.countdown,
      scanning_foot: data.scanning_foot,
      processing: data.processing,
      active: data.active,
      lastSeen: data.last_seen,
    };
  };

  useEffect(() => {
    if (connectionState.status !== ConnectionStatus.CONNECTED) return;

    const deactivateCode = async () => {
      // Only the winning session remains in sessionData once connected
      for (const session of sessionDataRef.current?.sessions ?? []) {
        try {
          if (session.activeMapRef) await remove(session.activeMapRef);
          if (session.inactiveMapRef) {
            await set(session.inactiveMapRef, sessionDataRef.current!.uuid);
          }
        } catch (error) {
          console.error(`Deactivating code failed (${session.name}):`, error);
        }
      }
    };

    deactivateCode();
  }, [connectionState.status]);

  const detachListeners = (names?: string[]) => {
    Object.keys(unsubscribersRef.current).forEach((name) => {
      if (names && !names.includes(name)) return;
      unsubscribersRef.current[name]();
      delete unsubscribersRef.current[name];
    });
  };

  const closeSessions = async (uuid: string, sessions: BackendSession[]) => {
    await Promise.all(
      sessions.map(async (session) => {
        try {
          await update(session.sessionRef, { closed: true });
          if (session.activeMapRef) await remove(session.activeMapRef);
          if (session.inactiveMapRef) await set(session.inactiveMapRef, uuid);
        } catch (error) {
          console.error(`Closing session failed (${session.name}):`, error);
        }
      })
    );
  };

  const clearInactiveTimer = () => {
    if (inactiveTimerRef.current) {
      clearTimeout(inactiveTimerRef.current);
      inactiveTimerRef.current = null;
    }
  };

  // Mobile sets active=false on any disconnect (incl. transient ping failures)
  // and flips it back to true on reconnect. Only end the web session if it
  // stays false for MOBILE_INACTIVE_TIMEOUT_MS.
  const handleMobileActivity = (
    sessionRef: any,
    parsedData: ReturnType<typeof parseSessionData>,
  ) => {
    if (parsedData.status !== ConnectionStatus.CONNECTED || parsedData.active !== false) {
      clearInactiveTimer();
      return;
    }
    if (inactiveTimerRef.current) return;

    const lastSeenAtStart = parsedData.lastSeen;

    inactiveTimerRef.current = setTimeout(async () => {
      inactiveTimerRef.current = null;
      try {
        const latest = (await get(sessionRef)).val();
        if (latest) {
          const latestParsed = parseSessionData(latest);
          // Still inactive only if active is false and last_seen is unchanged
          if (
            latestParsed.status !== ConnectionStatus.CONNECTED ||
            latestParsed.active !== false ||
            latestParsed.lastSeen !== lastSeenAtStart
          ) return;
          // WithActiveFirebaseSessionHOC redirects on DISCONNECTED
          setConnectionState((prev) => ({
            ...prev,
            ...latestParsed,
            status: ConnectionStatus.DISCONNECTED,
          }));
        }
      } catch (error) {
        console.error("Mobile inactivity check failed:", error);
      }
    }, MOBILE_INACTIVE_TIMEOUT_MS);
  };

  const handleSessionSnapshot = (name: string, sessionRef: DatabaseReference, data: any) => {
    if (!data) return;

    if (winnerRef.current && winnerRef.current !== name) return;

    if (!winnerRef.current) {
      // Not connected yet on this account; keep waiting for the scanning app
      if (!data.didConnect) return;

      // The scanning app picked this account: discard the others
      winnerRef.current = name;
      const current = sessionDataRef.current;
      if (current) {
        const losers = current.sessions.filter((s) => s.name !== name);
        detachListeners(losers.map((s) => s.name));
        commitSessionData({
          uuid: current.uuid,
          sessions: current.sessions.filter((s) => s.name === name),
        });
        closeSessions(current.uuid, losers);
      }
    }

    const parsedData = parseSessionData(data);
    handleMobileActivity(sessionRef, parsedData);
    setConnectionState((prev) => ({
      ...prev,
      ...parsedData,
    }));
  };

  const setupSessionListeners = (sessions: BackendSession[]) => {
    sessions.forEach((session) => {
      unsubscribersRef.current[session.name] = onValue(session.sessionRef, (snapshot) => {
        handleSessionSnapshot(session.name, session.sessionRef, snapshot.val());
      });
    });
  };

  const handleConnectionError = (error: any, errorMessage: string) => {
    console.error(errorMessage, error);
    setConnectionState((prev) => ({
      ...prev,
      status: ConnectionStatus.DISCONNECTED,
      result: null,
      sessionError: null,
      connectionError: error.message,
      instruction: null,
      countdown: null,
      scanning_foot: null,
      processing: false,
    }));
  };

  const deactivateSession = async (session: SessionData | null) => {
    clearInactiveTimer();
    detachListeners();
    winnerRef.current = null;
    if (session) {
      commitSessionData(null);
      await closeSessions(session.uuid, session.sessions);
    }
  };

  const updateSessionRef = async (data: Record<string, any>) => {
    await Promise.all(
      (sessionDataRef.current?.sessions ?? []).map((session) =>
        update(session.sessionRef, { ...data })
      )
    );
  };

  const signInBackends = async (): Promise<Backend[]> => {
    const results = await Promise.all(
      BACKENDS.map(async (backend) => {
        try {
          await signInAnonymously(backend.auth);
          return backend;
        } catch (error) {
          console.error(`Sign in failed (${backend.name}):`, error);
          return null;
        }
      })
    );
    const ready = results.filter((b): b is Backend => b !== null);
    if (ready.length === 0) throw new Error("Unable to sign in to Firebase");
    return ready;
  };

  const loadExistingSession = async (uuid: string) => {
    try {
      await deactivateSession(sessionDataRef.current);
      const ready = await signInBackends();

      // The session lives in whichever account created it
      let found: { backend: Backend; sessionRef: DatabaseReference; data: any } | null = null;
      for (const backend of ready) {
        const sessionRef = ref(backend.database, `connections/all/${uuid}`);
        const snapshot = await get(sessionRef);
        if (snapshot.exists()) {
          found = { backend, sessionRef, data: snapshot.val() };
          break;
        }
      }

      if (!found) {
        throw new Error(`Session ${uuid} not found`);
      }

      if (found.data.closed) {
        throw new Error(`Session is closed`);
      }

      const session: BackendSession = {
        name: found.backend.name,
        sessionRef: found.sessionRef,
        activeMapRef: null,
        inactiveMapRef: null,
      };

      winnerRef.current = session.name;
      setGASessionUuid(uuid);
      commitSessionData({ uuid, sessions: [session] });

      setConnectionState(parseSessionData(found.data));
      setupSessionListeners([session]);
    } catch (error) {
      handleConnectionError(error, "Load session failed:");
    }
  };

  const resetConnectionState = () => {
    setConnectionState({
      code: null,
      status: ConnectionStatus.INITIALIZING,
      result: null,
      sessionError: null,
      connectionError: null,
      instruction: null,
      countdown: null,
      scanning_foot: null,
      processing: false,
    });
  };

  const setupConnection = async () => {
    try {
      await deactivateSession(sessionDataRef.current);
      const ready = await signInBackends();

      const code = await generateUniqueCode(ready);
      const uuid = generateUUID();

      const initialSessionData = {
        code,
        active: true,
        didConnect: false,
        result: null,
        instruction: null,
        countdown: null,
        scanning_foot: null,
        processing: false,
        codeCreatedAt: serverTimestamp(),
      };

      // Same code and uuid in every account so the scanning app can resolve it in either
      const created: BackendSession[] = [];
      let lastError: unknown = null;
      await Promise.all(
        ready.map(async (backend) => {
          const session: BackendSession = {
            name: backend.name,
            sessionRef: ref(backend.database, `connections/all/${uuid}`),
            activeMapRef: ref(backend.database, `connections/active/${code}`),
            inactiveMapRef: ref(backend.database, `connections/inactive/${code}`),
          };
          try {
            await set(session.sessionRef, initialSessionData);
            await set(session.activeMapRef!, uuid);
            created.push(session);
          } catch (error) {
            console.error(`Creating session failed (${backend.name}):`, error);
            lastError = error;
          }
        })
      );

      if (created.length === 0) {
        throw lastError ?? new Error("Unable to create session");
      }

      setGASessionUuid(uuid);
      commitSessionData({ uuid, sessions: created });

      setConnectionState((prev) => ({
        ...prev,
        code,
        status: ConnectionStatus.INITIALIZING,
        sessionError: null,
        connectionError: null,
        instruction: null,
        countdown: null,
        scanning_foot: null,
        processing: false
      }));

      setupSessionListeners(created);
    } catch (error) {
      handleConnectionError(error, "Setup failed:");
    }
  };

  const setupNewConnection = async () => {
    resetConnectionState();
    await setupConnection();
  };

  useEffect(() => {
    if (options?.initialSessionUuid) {
      loadExistingSession(options.initialSessionUuid);
    } else {
      setupNewConnection();
    }

    const handleBeforeUnload = () => {
      deactivateSession(sessionDataRef.current);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      deactivateSession(sessionDataRef.current);
    };
  }, []);

  return {
    connectionState,
    setupNewConnection,
    updateSessionRef,
    uuid: sessionData?.uuid ?? null,
  };
};
